import React, { useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { Button } from '../ui/Button';
import { useUserRole, ROLES } from '../../context/UserRoleContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

export const Header = () => {
  const { role, setRole, session, appUser, signOut } = useUserRole();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileName = appUser?.name || 'Bruker';
  const profileInitials = profileName
    .split(/\s+/)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  // Simulert søkeresultat
  const searchResults = [
    { type: 'Kunde', title: 'TechCorp AS', path: '/dashboard/health' },
    { type: 'Prosjekt', title: 'Utvikling Kartlegging 2024', path: '/dashboard/projects' },
    { type: 'Rapport', title: 'Q2 Strategisk Rapport', path: '/dashboard/reports' },
    { type: 'Workshop', title: 'Psykologisk Trygghet', path: '/dashboard/workshops' }
  ].filter(res => res.title.toLowerCase().includes(searchQuery.toLowerCase()));

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <header className="bg-white border-b border-slate-200 h-20 flex items-center justify-between px-8 sticky top-0 z-[100] shadow-[0_1px_0_rgba(16,45,80,0.02)]">
      <div className="flex items-center gap-6 flex-1">
        <div className="relative max-w-md w-full">
          <SafeIcon name="Search" className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowSearchDropdown(e.target.value.length > 0);
            }}
            onBlur={() => setTimeout(() => setShowSearchDropdown(false), 200)}
            placeholder="Søk i kunder, prosjekter, rapporter..."
            className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-all font-medium"
          />

          <AnimatePresence>
            {showSearchDropdown && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden"
              >
                <div className="p-2">
                  {searchResults.length > 0 ? searchResults.map((res, i) => (
                    <button
                      key={i}
                      onClick={() => navigate(res.path)}
                      className="w-full flex items-center justify-between p-3 hover:bg-slate-50 rounded-xl transition-colors text-left"
                    >
                      <div>
                        <p className="text-xs font-bold text-slate-900">{res.title}</p>
                        <p className="text-[10px] text-slate-400 uppercase font-black">{res.type}</p>
                      </div>
                      <SafeIcon name="ArrowRight" className="text-slate-300" />
                    </button>
                  )) : (
                    <div className="p-4 text-center text-xs text-slate-400 italic">Ingen resultater funnet</div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Role Switcher (Kun for Demo) */}
        {!session && (
          <div className="hidden xl:flex bg-slate-100 p-1 rounded-xl">
            {Object.values(ROLES).map(r => (
              <button
                key={r}
                onClick={() => setRole(r)}
                className={`px-3 py-1.5 text-[9px] font-black uppercase rounded-lg transition-all ${
                  role === r ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                {r.replace('_', ' ')}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-4">
        {/* Quick Actions */}
        <div className="hidden 2xl:flex items-center gap-2 mr-4 pr-4 border-r border-slate-100">
          <Button variant="ghost" size="sm" icon={<SafeIcon name="FilePlus" />} onClick={() => navigate('/dashboard/reports')}>Rapport</Button>
          <Button variant="ghost" size="sm" icon={<SafeIcon name="Target" />} onClick={() => navigate('/dashboard/90-day-plan')}>90-Day Plan</Button>
          <Button variant="ghost" size="sm" icon={<SafeIcon name="Users" />} onClick={() => navigate('/dashboard/workshops')}>Workshop</Button>
        </div>

        <button className="relative p-2.5 text-slate-400 hover:text-indigo-600 hover:bg-slate-50 rounded-xl transition-all">
          <SafeIcon name="Bell" className="text-xl" />
          <span className="absolute top-2.5 right-2.5 w-2 h-2 bg-rose-500 rounded-full ring-2 ring-white"></span>
        </button>

        <div className="h-8 w-px bg-slate-200 mx-2"></div>

        <div className="relative z-[100]">
          <button
            type="button"
            aria-expanded={profileMenuOpen}
            aria-label="Åpne profilmeny"
            onClick={() => setProfileMenuOpen((open) => !open)}
            className="flex items-center gap-3 pl-2 cursor-pointer group"
          >
            <div className="text-right hidden sm:block">
              <p className="text-xs font-bold text-slate-900 leading-none">{profileName}</p>
              <p className="text-[10px] text-slate-400 font-bold uppercase mt-1.5 tracking-wider">{role.replace('_', ' ')}</p>
            </div>
            <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center text-white font-black text-sm shadow-lg shadow-indigo-100 group-hover:scale-105 transition-transform">
              {profileInitials}
            </div>
          </button>

          {profileMenuOpen && (
            <div className="absolute right-0 top-full mt-2 z-[100] w-64 rounded-xl border border-slate-200 bg-white p-4 shadow-xl">
              <p className="text-sm font-bold text-slate-900">{profileName}</p>
              <p className="mt-1 break-all text-xs text-slate-500">{appUser?.email || ''}</p>
              <p className="mt-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">{role.replace('_', ' ')}</p>
              <button
                type="button"
                onClick={() => {
                  setProfileMenuOpen(false);
                  handleLogout();
                }}
                className="mt-4 w-full rounded-lg border border-slate-200 px-3 py-2 text-left text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
              >
                Logg ut
              </button>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={handleLogout}
          aria-label="Logg ut"
          title="Logg ut"
          className="p-2.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all"
        >
          <SafeIcon name="LogOut" className="text-xl" />
        </button>
      </div>
    </header>
  );
};